import { useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { useLeads } from '@/leads/LeadsProvider';
import { PROCEDIMENTOS_PADRAO, TEMPERATURAS } from '@/lib/constants';
import { origensAtivas } from '@/leads/origens';
import { apenasSdrs } from '@/equipe/funcoes';
import { criarLead } from '@/services/leadsService';
import { useAuth } from '@/auth/AuthProvider';
import { ehGestor } from '@/leads/posseTela';

const VAZIO = {
  nome: '',
  telefone: '',
  email: '',
  cidade: '',
  // VH: quem cria à mão sabe de onde veio — começa vazio e é obrigatório.
  origem: '',
  procedimento: '',
  temperatura: 'morno',
  sdrId: '',
  observacoes: '',
};

export default function DialogoNovoLead({ aberto, aoFechar }) {
  const { clinicaId, equipe, autor, abrirLead, clinica } = useLeads();
  /**
   * ★★★ PC-6 (N1) — a SDR cria lead para SI ou sem SDR; só o gestor escolhe outra pessoa. As rules
   * impõem o mesmo no `create`. ★ A SDR vem PRÉ-SELECIONADA (antes vinha vazio, e o vazio gravava
   * lead SEM SDR apesar do rótulo "Atribuir a mim" — `''` não é nulo para o `??` do `criarLead`).
   */
  const { papel } = useAuth();
  const gestor = ehGestor(papel);
  const inicial = () => ({ ...VAZIO, sdrId: gestor ? '' : (autor?.id ?? '') });
  const [dados, setDados] = useState(inicial);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const mudar = (campo) => (e) => setDados((d) => ({ ...d, [campo]: e.target.value }));

  function fechar() {
    setDados(inicial());
    setErro(null);
    setAviso(null);
    aoFechar();
  }

  async function salvar(evento) {
    evento.preventDefault();
    setErro(null);
    setAviso(null);
    setSalvando(true);
    try {
      const sdr = equipe.find((u) => u.id === dados.sdrId);
      const resultado = await criarLead(
        clinicaId,
        { ...dados, sdrNome: sdr?.nome ?? '', etapa: 'novo' },
        autor,
      );
      if (resultado.duplicado) {
        // Telefone já existe: o contato foi registrado na timeline do lead
        // original em vez de criar um duplicado (PLANO 4.3).
        setAviso(
          `Este telefone já pertence ao lead "${resultado.lead.nome}". O novo contato foi registrado na timeline dele.`,
        );
        setSalvando(false);
        return;
      }
      abrirLead(resultado.id);
      fechar();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível criar o lead.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialogo titulo="Novo lead" aberto={aberto} aoFechar={fechar} largura={520}>
      <form className="formulario" onSubmit={salvar}>
        <div className="campo">
          <label htmlFor="nl-nome">Nome</label>
          <input id="nl-nome" required value={dados.nome} onChange={mudar('nome')} />
        </div>

        <div className="linha">
          <div className="campo">
            <label htmlFor="nl-telefone">Telefone</label>
            <input
              id="nl-telefone"
              required
              placeholder="(51) 99123-0001"
              value={dados.telefone}
              onChange={mudar('telefone')}
            />
          </div>
          <div className="campo">
            <label htmlFor="nl-origem">Origem</label>
            <select id="nl-origem" required value={dados.origem} onChange={mudar('origem')}>
              <option value="">Escolha…</option>
              {origensAtivas(clinica?.config).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="linha">
          <div className="campo">
            <label htmlFor="nl-procedimento">Procedimento de interesse</label>
            <select id="nl-procedimento" value={dados.procedimento} onChange={mudar('procedimento')}>
              <option value="">Não informado</option>
              {PROCEDIMENTOS_PADRAO.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="nl-temperatura">Temperatura</label>
            <select id="nl-temperatura" value={dados.temperatura} onChange={mudar('temperatura')}>
              {TEMPERATURAS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="linha">
          <div className="campo">
            <label htmlFor="nl-email">E-mail</label>
            <input id="nl-email" type="email" value={dados.email} onChange={mudar('email')} />
          </div>
          <div className="campo">
            <label htmlFor="nl-cidade">Cidade</label>
            <input id="nl-cidade" value={dados.cidade} onChange={mudar('cidade')} />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="nl-sdr">SDR responsável</label>
          <select id="nl-sdr" value={dados.sdrId} onChange={mudar('sdrId')}>
            {gestor ? (
              <>
                <option value="">Sem responsável</option>
                {apenasSdrs(equipe).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}
                  </option>
                ))}
              </>
            ) : (
              <>
                <option value={autor?.id ?? ''}>{autor?.nome ? `${autor.nome} (eu)` : 'Eu'}</option>
                <option value="">Sem responsável</option>
              </>
            )}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="nl-obs">Observações</label>
          <textarea id="nl-obs" rows={2} value={dados.observacoes} onChange={mudar('observacoes')} />
        </div>

        {aviso && <p className="aviso">{aviso}</p>}
        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={fechar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={salvando}>
            {salvando ? 'Salvando…' : 'Criar lead'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
